<template>
  <span v-if="entityId && normalizedCount" class="text-change-history-button">
    <span v-if="showLabel">{{ $t('Change history') }}: </span>

    <a href="#"
       class="p-link table-link-icon"
       draggable="false"
       @click.prevent="toggleHistory">
      <i class="ti-time"/>
      <span class="history-count">{{ normalizedCount }}</span>
    </a>

    <OverlayPanel ref="historyOverlay"
                  style="line-height: 1.4;"
                  :autoZIndex="$store.state.firstLayerIsOpened">
      <BillingFeatureHint
          v-if="billingEntitlementsReady && !canUseDetailedChangeHistory"
          feature="detailed_change_history"
          message-key="billing.features.detailed_change_history.availableHint"
          class="p-d-block"/>

      <template v-else-if="historyRows.length">
        <div v-for="(history, index) of historyRows"
             :key="`${history.created_at}-${index}`"
             class="p-mb-2 p-mr-3 history-entry">
          <div class="p-mb-2">
            {{ formatBy(history.created_at, history.creator) }}
          </div>
          <div class="balanced-text word-break history-value">
            <span v-if="hasValue(history.value)">{{ history.value }}</span>
            <i v-else class="ti-minus"/>
          </div>
          <hr v-if="index !== historyRows.length - 1">
        </div>
      </template>

      <div v-else-if="historyLoaded">
        {{ $t('No data available') }}
      </div>
    </OverlayPanel>
  </span>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from '@/mixins/formatMixins'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureHint from '@/components/billing/BillingFeatureHint.vue'

export default {
  name: 'TextChangeHistoryButton',

  components: { BillingFeatureHint },

  mixins: [formatMixins, showErrorsMixins, billingFeaturesMixins],

  props: {
    entity: {
      type: String,
      required: true,
      validator: value => ['order', 'offer'].includes(value),
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
    kind: {
      type: String,
      required: true,
      validator: value => [
        'customer_comment',
        'staff_comment',
        'recommendation',
      ].includes(value),
    },
    count: {
      type: [Number, String],
      default: 0,
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      historyRows: [],
      historyLoaded: false,
      isLoading: false,
      requestId: 0,
    }
  },

  computed: {
    normalizedCount() {
      const count = Number(this.count)
      return Number.isFinite(count) && count > 0 ? count : 0
    },
  },

  watch: {
    entity: 'resetHistory',
    entityId: 'resetHistory',
    kind: 'resetHistory',
    count: 'resetHistory',
  },

  methods: {
    async toggleHistory(event) {
      if (this.isLoading) return

      const overlay = this.$refs.historyOverlay
      const domEvent = event?.originalEvent ?? event
      const target = domEvent?.currentTarget

      if (!overlay || !domEvent || !target) return

      if (overlay.visible) {
        overlay.hide()
        return
      }

      const entityId = this.entityId

      if (
          this.billingEntitlementsReady &&
          this.canUseDetailedChangeHistory &&
          !this.historyLoaded
      ) {
        await this.loadHistory()
      }

      await this.$nextTick()

      if (this.entityId === entityId) {
        overlay.show(domEvent, target)
      }
    },

    async loadHistory() {
      const requestId = ++this.requestId
      this.isLoading = true

      try {
        const { data, status } = await httpClient.post(
            `${this.entity}/get-change-history`,
            {
              id: Number(this.entityId),
              kind: this.kind,
            }
        )

        if (requestId !== this.requestId) return

        if (status === 200 && data?.success && Array.isArray(data.history)) {
          this.historyRows = data.history
          this.historyLoaded = true
          return
        }

        this.showUnknownError()
      } catch (error) {
        if (requestId !== this.requestId) return
        this.showError(error)
      } finally {
        if (requestId === this.requestId) {
          this.isLoading = false
        }
      }
    },

    resetHistory() {
      this.requestId += 1
      this.historyRows = []
      this.historyLoaded = false
      this.isLoading = false
      this.$refs.historyOverlay?.hide?.()
    },

    hasValue(value) {
      return value !== null && value !== undefined && value !== ''
    },
  },
}
</script>

<style scoped lang="scss">
.history-count {
  //position: relative;
  //top: -1px;
  margin-left: 2px;
  font-size: 0.9em;
  font-variant-numeric: normal;
}

.history-entry:last-child {
  margin-bottom: 0 !important;
}

.history-value {
  max-width: 600px;
  font-size: 0.92em;
  white-space: pre-line;
}
</style>